/**
 * EntitlementViewer – Elm-style UI for the Ent tab (Tier 1 / Tier 2 / Tier 3).
 *
 * Model: local UI state (active sub-tab).
 * View: pure render from model + props.
 * Update: reducer for SetActiveSubTab; Tier 2 entity type is controlled via props.
 */

import React, { useReducer } from 'react';
import { Box, Text, Tabs, Select, Alert, Accordion } from '@mantine/core';
import { LoadingState } from '../common/LoadingState';
import { Tier2SchemaTabView } from '../common/Tier2SchemaTabView';
import { JsonViewer } from '../common/JsonViewer';
import type { EntitlementViewerModel, EntitlementViewerMsg, EntitlementViewerProps } from './EntitlementViewer.types';

// --- Init ---
const initialModel: EntitlementViewerModel = {
  activeSubTab: 0,
};

// --- Update ---
export function update(model: EntitlementViewerModel, msg: EntitlementViewerMsg): EntitlementViewerModel {
  switch (msg.type) {
    case 'SetActiveSubTab':
      return { ...model, activeSubTab: msg.payload };
    case 'SetTier2EntityType':
      return model; // Tier2 selection is controlled by parent; no local state
    default:
      return model;
  }
}

// --- Component (reducer + view) ---
export function EntitlementViewer(props: EntitlementViewerProps): React.ReactElement {
  const [model, dispatch] = useReducer(update, initialModel);

  const handleTabChange = (v: string | null) => {
    const n = Number(v ?? 0);
    if (n >= 0 && n <= 2) dispatch({ type: 'SetActiveSubTab', payload: n as 0 | 1 | 2 });
  };

  return (
    <Box>
      <Tabs value={String(model.activeSubTab)} onChange={handleTabChange} mb="md">
        <Tabs.List>
          <Tabs.Tab value="0">Tier 1</Tabs.Tab>
          <Tabs.Tab value="1">Tier 2</Tabs.Tab>
          <Tabs.Tab value="2">Tier 3</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="0">
          <Text size="sm" fw={600} c="dimmed" mb="xs">
            GET /mgmt/Entitlements/entity (entity types / nav)
          </Text>
          {props.tier1Data == null ? (
            <Alert color="blue">
              No tier 1 data in cache. Reload the app or use a table to load entitlements.
            </Alert>
          ) : (
            <JsonViewer
              data={props.tier1Data}
              title="Entity entitlements"
              description="Tier 1 response: requestor, entity sets, and per-entity entitlements."
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="1">
          {props.tier2TypesInPlay.length > 1 ? (
            <Box mb="md">
              <Select
                label="Tier 2 for"
                value={props.tier2SelectedEntityType || props.currentEntityTypeKey}
                onChange={(v) =>
                  props.onTier2EntityTypeChange(v || props.currentEntityTypeKey)
                }
                data={props.tier2TypesInPlay.map((typeKey) => {
                  const label =
                    props.entityTypeOptions?.find(
                      (e) => (e.name || e.id || '').toLowerCase() === typeKey
                    )?.name ?? typeKey;
                  return { value: typeKey, label };
                })}
                size="sm"
                miw={220}
              />
            </Box>
          ) : null}
          <Text size="sm" fw={600} c="dimmed" mb="xs">
            {`GET /mgmt/Entitlements/entity/${(
              props.tier2SelectedEntityType || props.currentEntityTypeKey
            ).toLowerCase()}`}
          </Text>
          {props.tier2Error ? (
            <Alert color="red" title="Tier 2 error" mb="md">
              {props.tier2Error}
            </Alert>
          ) : null}
          {!props.tier2Error && props.tier2Loading ? (
            <LoadingState variant="centered" label="Loading tier 2…" size="sm" />
          ) : props.tier2Data == null && !props.tier2Error ? (
            <Alert color="blue">No tier 2 data for this selection.</Alert>
          ) : props.tier2Data != null ? (
            <Accordion multiple defaultValue={['schema', 'full']} variant="contained" mb="md">
              <Accordion.Item value="schema">
                <Accordion.Control>
                  <Text size="sm" fw={600}>
                    Schema / bindings
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Tier2SchemaTabView
                    entityType={(
                      props.tier2SelectedEntityType || props.currentEntityTypeKey
                    ).toLowerCase()}
                    tier2Data={props.tier2Data}
                    tier2Loading={props.tier2Loading}
                  />
                </Accordion.Panel>
              </Accordion.Item>
              <Accordion.Item value="full">
                <Accordion.Control>
                  <Text size="sm" fw={600}>
                    Full response
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <JsonViewer
                    data={props.tier2Data}
                    description="Raw tier 2 response for the selected entity type."
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value="2">
          <Text size="sm" fw={600} c="dimmed" mb="xs">
            {props.entityId
              ? `GET /mgmt/Entitlements/entity/${(props.entityType ?? '').toLowerCase()}/${props.entityId}`
              : 'Tier 3 (target entitlement) requires an entity id.'}
          </Text>
          {props.tier3Error ? (
            <Alert color="red" title="Tier 3 error" mb="md">
              {props.tier3Error}
            </Alert>
          ) : null}
          {props.targetEntitlement ? (
            <JsonViewer
              data={props.targetEntitlement}
              title="Target entitlement"
              description="Tier 3 response for this entity."
            />
          ) : !props.tier3Error ? (
            <Alert color="blue">
              No tier 3 data (target entitlement). In view mode it is loaded with the entity; in
              edit mode it is loaded when opening for an entity.
            </Alert>
          ) : null}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

export { initialModel };
export type { EntitlementViewerModel, EntitlementViewerMsg, EntitlementViewerProps } from './EntitlementViewer.types';
